import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import toast from 'react-hot-toast';
import { getMonthlySalarySummary, getAvailablePaidMonths, MonthlySalarySummary, AvailableMonth } from '../service/paymentService';
import { getMonthName } from '../service/attendanceService';
import { FiTrendingUp } from 'react-icons/fi';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setSalarySummarySelection } from '../../../store/slices/attendanceSlice';

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.light.border.light};
  border-top: 4px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto ${theme.spacing[4]};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SelectorContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  align-items: center;
  flex-wrap: wrap;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  min-width: 150px;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const Select = styled.select`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-top: ${theme.spacing[4]};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[700]} 100%);
  color: ${theme.colors.white};
`;

const TableHeaderCell = styled.th`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  text-align: left;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  white-space: nowrap;
`;

const TableBody = styled.tbody`
  & tr {
    border-bottom: 1px solid ${theme.colors.light.border.light};
    
    &:hover {
      background: ${theme.colors.light.background.secondary};
    }
  }
`;

const TableCell = styled.td`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
`;

const GuardInfoCell = styled(TableCell)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const GuardImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const GuardDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const GuardName = styled.div`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const GuardId = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

const Badge = styled.span<{ variant?: 'success' | 'danger' | 'warning' | 'info' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant = 'info' }) => {
    if (variant === 'success') return `${theme.colors.accent.success}20`;
    if (variant === 'danger') return `${theme.colors.accent.danger}20`;
    if (variant === 'warning') return `${theme.colors.accent.warning}20`;
    return `${theme.colors.primary[500]}20`;
  }};
  color: ${({ variant = 'info' }) => {
    if (variant === 'success') return theme.colors.accent.success;
    if (variant === 'danger') return theme.colors.accent.danger;
    if (variant === 'warning') return theme.colors.accent.warning;
    return theme.colors.primary[600];
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const SummaryCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  text-align: center;
`;

const SummaryValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const SummaryLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

interface SalarySummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  initialYear?: number;
  initialMonth?: number;
}

const SalarySummaryModal: React.FC<SalarySummaryModalProps> = ({
  isOpen,
  onClose,
  companyId,
  initialYear,
  initialMonth,
}) => {
  const dispatch = useAppDispatch();
  const reduxYear = useAppSelector((state) => state.attendance.salarySummarySelectedYear);
  const reduxMonth = useAppSelector((state) => state.attendance.salarySummarySelectedMonth);
  
  const [availableMonths, setAvailableMonths] = useState<AvailableMonth[]>([]);
  const [summary, setSummary] = useState<MonthlySalarySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMonths, setLoadingMonths] = useState(false);
  const [monthsLoaded, setMonthsLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Determine selected year and month (priority: initial > redux > latest available)
  const selectedYear = useMemo(() => {
    if (initialYear) return initialYear;
    if (reduxYear) return reduxYear;
    return availableMonths.length > 0 ? availableMonths[0].year : null;
  }, [initialYear, reduxYear, availableMonths]);

  const selectedMonth = useMemo(() => {
    if (initialMonth) return initialMonth;
    if (reduxMonth) return reduxMonth;
    return availableMonths.length > 0 ? availableMonths[0].month : null;
  }, [initialMonth, reduxMonth, availableMonths]);

  // Get unique years from available months
  const availableYears = useMemo(() => {
    const yearSet = new Set(availableMonths.map(m => m.year));
    return Array.from(yearSet).sort((a, b) => b - a); // Newest first
  }, [availableMonths]);

  // Get months for selected year
  const availableMonthsForYear = useMemo(() => {
    if (!selectedYear) return [];
    return availableMonths
      .filter(m => m.year === selectedYear)
      .sort((a, b) => b.month - a.month); // Newest first
  }, [availableMonths, selectedYear]);

  // Load available paid months when modal opens
  useEffect(() => {
    if (isOpen && companyId) {
      // Reset state when modal opens
      setMonthsLoaded(false);
      setAvailableMonths([]);
      loadAvailableMonths();
    } else if (!isOpen) {
      // Reset when modal closes
      setMonthsLoaded(false);
      setAvailableMonths([]);
      setSummary([]);
    }
  }, [isOpen, companyId]);

  // Load summary when selection changes
  useEffect(() => {
    if (isOpen && selectedYear && selectedMonth && companyId) {
      loadSummary();
    }
  }, [isOpen, selectedYear, selectedMonth, companyId]);

  const loadAvailableMonths = async () => {
    setLoadingMonths(true);
    setMonthsLoaded(false);
    try {
      const months = await getAvailablePaidMonths(companyId);
      console.log('Loaded available months:', months.length);
      setAvailableMonths(months);
      // Set monthsLoaded after setting availableMonths
      setMonthsLoaded(true);
    } catch (error: any) {
      console.error('Error loading available months:', error);
      toast.error('Failed to load available months: ' + (error.message || 'Unknown error'));
      setAvailableMonths([]); // Set empty array on error
      setMonthsLoaded(true); // Still mark as loaded even on error
    } finally {
      setLoadingMonths(false);
    }
  };

  const loadSummary = async () => {
    if (!selectedYear || !selectedMonth) return;
    
    setLoading(true);
    try {
      const data = await getMonthlySalarySummary(selectedYear, selectedMonth, companyId);
      setSummary(data);
    } catch (error: any) {
      console.error('Error loading salary summary:', error);
      toast.error('Failed to load salary summary: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (year: number) => {
    // Find first available month for this year
    const firstMonth = availableMonths.find(m => m.year === year);
    if (firstMonth) {
      dispatch(setSalarySummarySelection({ year, month: firstMonth.month }));
    }
  };

  const handleMonthChange = (month: number) => {
    if (selectedYear) {
      dispatch(setSalarySummarySelection({ year: selectedYear, month }));
    }
  };

  const filteredSummary = useMemo(() => {
    if (!searchTerm.trim()) return summary;
    const term = searchTerm.toLowerCase();
    return summary.filter((s) => {
      const name = s.guard.name?.toLowerCase() || '';
      const guardId = s.guard.guardId?.toLowerCase() || '';
      return name.includes(term) || guardId.includes(term);
    });
  }, [summary, searchTerm]);

  const totals = useMemo(() => {
    return {
      totalSalary: filteredSummary.reduce((sum, s) => sum + s.salary, 0),
      totalAdvance: filteredSummary.reduce((sum, s) => sum + s.advanceSalary, 0),
      totalPaid: filteredSummary.reduce((sum, s) => sum + (s.paidAmount || 0), 0),
      totalDeduction: filteredSummary.reduce((sum, s) => sum + (s.deductionAmount || 0), 0),
      totalBalance: filteredSummary.reduce((sum, s) => sum + (s.balance || 0), 0),
      paidCount: filteredSummary.filter((s) => s.paymentStatus === 'paid').length,
    };
  }, [filteredSummary]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Salary Summary"
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <SelectorContainer>
        <SelectWrapper>
          <Label>Year</Label>
          <Select 
            value={selectedYear || ''} 
            onChange={(e) => handleYearChange(Number(e.target.value))}
            disabled={loadingMonths || availableYears.length === 0}
          >
            {availableYears.length === 0 ? (
              <option value="">No paid months available</option>
            ) : (
              availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))
            )}
          </Select>
        </SelectWrapper>
        <SelectWrapper>
          <Label>Month</Label>
          <Select 
            value={selectedMonth || ''} 
            onChange={(e) => handleMonthChange(Number(e.target.value))}
            disabled={loadingMonths || !selectedYear || availableMonthsForYear.length === 0}
          >
            {availableMonthsForYear.length === 0 ? (
              <option value="">No paid months for this year</option>
            ) : (
              availableMonthsForYear.map((monthData) => (
                <option key={monthData.month} value={monthData.month}>
                  {getMonthName(monthData.month)}
                </option>
              ))
            )}
          </Select>
        </SelectWrapper>
        <div style={{ flex: 1, minWidth: 250 }}>
          <Label>Search Guard</Label>
          <Input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </SelectorContainer>

      {loadingMonths || !monthsLoaded ? (
        <LoadingContainer>
          <Spinner />
          <p style={{ color: theme.colors.light.text.secondary }}>Loading available months...</p>
        </LoadingContainer>
      ) : availableMonths.length === 0 ? (
        <EmptyState>
          <FiTrendingUp size={48} style={{ marginBottom: theme.spacing[4], opacity: 0.5 }} />
          <p style={{ fontSize: theme.typography.fontSize.lg, margin: 0 }}>
            No paid months available yet
          </p>
        </EmptyState>
      ) : loading ? (
        <LoadingContainer>
          <Spinner />
          <p style={{ color: theme.colors.light.text.secondary }}>Loading salary summary...</p>
        </LoadingContainer>
      ) : filteredSummary.length > 0 ? (
        <>
          <SummaryCards>
            <SummaryCard>
              <SummaryValue color={theme.colors.primary[600]}>
                Rs. {totals.totalSalary.toLocaleString()}
              </SummaryValue>
              <SummaryLabel>Total Salary</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={theme.colors.accent.warning}>
                Rs. {totals.totalAdvance.toLocaleString()}
              </SummaryValue>
              <SummaryLabel>Total Advance</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={theme.colors.accent.success}>
                Rs. {totals.totalPaid.toLocaleString()}
              </SummaryValue>
              <SummaryLabel>Total Paid</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={theme.colors.accent.danger}>
                Rs. {totals.totalDeduction.toLocaleString()}
              </SummaryValue>
              <SummaryLabel>Total Deduction</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={totals.totalBalance > 0 ? theme.colors.accent.danger : theme.colors.accent.success}>
                Rs. {totals.totalBalance.toLocaleString()}
              </SummaryValue>
              <SummaryLabel>Total Balance</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={theme.colors.primary[600]}>
                {totals.paidCount} / {filteredSummary.length}
              </SummaryValue>
              <SummaryLabel>Paid Guards</SummaryLabel>
            </SummaryCard>
          </SummaryCards>

          <TableWrapper>
            <StyledTable>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Guard</TableHeaderCell>
                  <TableHeaderCell>Salary</TableHeaderCell>
                  <TableHeaderCell>Advance</TableHeaderCell>
                  <TableHeaderCell>Paid Amount</TableHeaderCell>
                  <TableHeaderCell>Deduction</TableHeaderCell>
                  <TableHeaderCell>Balance</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {filteredSummary.map((item) => (
                  <tr key={item.guardId}>
                    <GuardInfoCell>
                      {item.guard.image ? (
                        <GuardImage src={item.guard.image} alt={item.guard.name} />
                      ) : (
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: theme.colors.light.background.tertiary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: theme.typography.fontSize.lg,
                            color: theme.colors.light.text.muted,
                          }}
                        >
                          {item.guard.name?.charAt(0) || '?'}
                        </div>
                      )}
                      <GuardDetails>
                        <GuardName>{item.guard.name || 'Unknown'}</GuardName>
                        <GuardId>{item.guard.guardId || 'No ID'}</GuardId>
                      </GuardDetails>
                    </GuardInfoCell>
                    <TableCell>
                      <strong>Rs. {item.salary.toLocaleString()}</strong>
                    </TableCell>
                    <TableCell>Rs. {item.advanceSalary.toLocaleString()}</TableCell>
                    <TableCell>
                      {item.paidAmount ? (
                        <strong style={{ color: theme.colors.accent.success }}>
                          Rs. {item.paidAmount.toLocaleString()}
                        </strong>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {item.deductionAmount ? (
                        <div>
                          <strong style={{ color: theme.colors.accent.danger }}>
                            Rs. {item.deductionAmount.toLocaleString()}
                          </strong>
                          {item.balanceReason && (
                            <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary, fontStyle: 'italic', marginTop: theme.spacing[1] }}>
                              {item.balanceReason}
                            </div>
                          )}
                        </div>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <strong style={{ color: (item.balance || 0) > 0 ? theme.colors.accent.danger : theme.colors.accent.success }}>
                        Rs. {(item.balance || 0).toLocaleString()}
                      </strong>
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.paymentStatus === 'paid' ? 'success' : item.paymentStatus === 'unpaid' ? 'danger' : 'info'}>
                        {item.paymentStatus === 'paid' ? 'Paid' : item.paymentStatus === 'unpaid' ? 'Unpaid' : 'Current'}
                      </Badge>
                    </TableCell>
                  </tr>
                ))}
              </TableBody>
            </StyledTable>
          </TableWrapper>
        </>
      ) : (
        <EmptyState>
          <FiTrendingUp size={48} style={{ marginBottom: theme.spacing[4], opacity: 0.5 }} />
          <p style={{ fontSize: theme.typography.fontSize.lg, margin: 0 }}>
            {selectedYear && selectedMonth 
              ? `No paid salary data found for ${getMonthName(selectedMonth)} ${selectedYear}`
              : 'Please select a month and year'}
          </p>
        </EmptyState>
      )}
    </Modal>
  );
};

export default SalarySummaryModal;

